import * as React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Nullable, OrgComponentProps } from '../typescriptHelpers';
import FormItem from 'antd/lib/form/FormItem';
import InputNumber from 'antd/lib/input-number';
import Text from 'antd/lib/typography/Text';
import Checkbox from 'antd/lib/checkbox';
import {
    RelativeTiming,
    LifecycleEventType,
    ReleaseScheduleCadenceStyle,
    AppPlatform,
    RelativeTimeTrigger,
} from '../types';
import Select from 'antd/lib/select';
import { useSelector } from 'react-redux';
import { RootState } from '../reducers/reducers';
import { selectScheduleSettings } from '../selectors/selectors';
import { useWatch } from 'antd/lib/form/Form';
import { Form } from 'antd/lib';
import { runwayColors } from './styles/globalStyles';
import { useRelativeTimeTriggerOptions } from '@/utils/hooks/useRelativeTimeTriggerOptions';
import Tooltip from 'antd/lib/tooltip';

const MULTI_SELECTOR_KEY = 'lifecycleEventType+waitForAllDestinations';

interface RelativeTimeTriggerSettingsProps extends OrgComponentProps {
    appId?: string;
    platform: AppPlatform;
    stepConfigId: Nullable<string>;
    initialValues: Partial<RelativeTimeTrigger>;
    parentFieldName?: string;
    isOrgLevelItem?: boolean;
    showAllowTriggeringBasedOnTargetDate?: boolean;
}

export const RelativeTimeTriggerSettings: React.FC<RelativeTimeTriggerSettingsProps> = ({
    appId,
    platform,
    stepConfigId,
    initialValues,
    parentFieldName = 'relativeTimeTrigger',
    isOrgLevelItem,
    showAllowTriggeringBasedOnTargetDate = false,
}: RelativeTimeTriggerSettingsProps) => {
    const form = Form.useFormInstance();
    const selectedLifecycleEventType = useWatch<LifecycleEventType>([parentFieldName, 'lifecycleEventType']);
    const selectedTiming = useWatch<RelativeTiming>([parentFieldName, 'timing']);
    const multiSelectorValue = useWatch<string>([parentFieldName, MULTI_SELECTOR_KEY]);
    const scheduleSettings = useSelector((state: RootState) => (appId ? selectScheduleSettings(state, appId) : null));
    const hasActiveSchedule =
        scheduleSettings ? scheduleSettings.scheduleCadence !== ReleaseScheduleCadenceStyle.Never : false;

    const shouldDisableBeforeOptionForRelativeTiming = React.useMemo(() => {
        if (isOrgLevelItem) return false;
        if (!hasActiveSchedule || !selectedLifecycleEventType) return true;

        const targetKickoff = scheduleSettings?.cadenceTargetDateSets?.[0]?.kickoffDayOfWeek;
        const targetSubmit = scheduleSettings?.cadenceTargetDateSets?.[0]?.submitDayOfWeek;
        const targetRelease = scheduleSettings?.cadenceTargetDateSets?.[0]?.releaseDayOfWeek;

        switch (selectedLifecycleEventType) {
            case LifecycleEventType.LifecycleEventTypeKickoff:
                return !targetKickoff;
            case LifecycleEventType.LifecycleEventTypeSubmit:
                return !targetSubmit;
            case LifecycleEventType.LifecycleEventTypeRelease:
                return !targetRelease;
            default:
                return false;
        }
    }, [isOrgLevelItem, hasActiveSchedule, scheduleSettings, selectedLifecycleEventType]);

    const {
        lifecycleEventOptions,
        relativeTimingOptions,
        parseSelectedLifecycleEventOption,
        convertToSelectedLifecycleEventValue,
    } = useRelativeTimeTriggerOptions(appId, platform, stepConfigId);

    React.useEffect(() => {
        const trigger = {
            enabled: true,
            relativeDays: initialValues.relativeDays ?? 0,
            relativeHours: initialValues.relativeHours ?? 0,
            relativeMinutes: initialValues.relativeMinutes ?? 0,
            timing: initialValues.timing || RelativeTiming.after,
            lifecycleEventType: initialValues.lifecycleEventType || LifecycleEventType.LifecycleEventTypeKickoff,
            waitForAllDestinations: initialValues.waitForAllDestinations ?? false,
            allowTriggeringBasedOnTargetDate: initialValues.allowTriggeringBasedOnTargetDate ?? false,
        };

        const initialSelectedEventOption = convertToSelectedLifecycleEventValue(
            trigger.lifecycleEventType,
            trigger.waitForAllDestinations,
        );

        trigger[MULTI_SELECTOR_KEY] = initialSelectedEventOption;
        form.setFieldValue(parentFieldName, trigger);
    }, [
        form,
        parentFieldName,
        initialValues.allowTriggeringBasedOnTargetDate,
        initialValues.lifecycleEventType,
        initialValues.relativeDays,
        initialValues.relativeHours,
        initialValues.relativeMinutes,
        initialValues.timing,
        initialValues.waitForAllDestinations,
        convertToSelectedLifecycleEventValue,
    ]);

    React.useEffect(() => {
        if (selectedTiming === RelativeTiming.before && shouldDisableBeforeOptionForRelativeTiming) {
            form.setFieldValue([parentFieldName, 'timing'], RelativeTiming.after);
        } else if (selectedTiming === RelativeTiming.onTime) {
            form.setFieldValue([parentFieldName, 'relativeDays'], 0);
            form.setFieldValue([parentFieldName, 'relativeHours'], 0);
            form.setFieldValue([parentFieldName, 'relativeMinutes'], 0);
        }
    }, [form, parentFieldName, selectedTiming, shouldDisableBeforeOptionForRelativeTiming]);

    React.useEffect(() => {
        if (multiSelectorValue) {
            const parsedValue = parseSelectedLifecycleEventOption(multiSelectorValue);
            form.setFieldValue([parentFieldName, 'lifecycleEventType'], parsedValue.lifecycleEventType);
            form.setFieldValue([parentFieldName, 'waitForAllDestinations'], parsedValue.waitForAllDestinations);
        }
    }, [form, parentFieldName, multiSelectorValue, parseSelectedLifecycleEventOption]);

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    columnGap: 12,
                    marginBottom: 0,
                    width: '100%',
                }}
            >
                <FormItem name={[parentFieldName, 'relativeDays']} style={{ marginBottom: 0, marginTop: 0 }}>
                    <InputNumber
                        min={0}
                        disabled={selectedTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">d</Text>}
                    />
                </FormItem>
                <FormItem name={[parentFieldName, 'relativeHours']} style={{ marginBottom: 0, marginTop: 0 }}>
                    <InputNumber
                        min={0}
                        max={23}
                        disabled={selectedTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">h</Text>}
                    />
                </FormItem>
                <FormItem name={[parentFieldName, 'relativeMinutes']} style={{ marginBottom: 0, marginTop: 0 }}>
                    <InputNumber
                        min={0}
                        max={59}
                        disabled={selectedTiming === RelativeTiming.onTime}
                        style={timeInputStyle}
                        suffix={<Text type="secondary">m</Text>}
                    />
                </FormItem>
                <FormItem
                    name={[parentFieldName, 'timing']}
                    style={{
                        marginBottom: 0,
                        minWidth: 200,
                        width: '100%',
                        flex: 1,
                    }}
                >
                    <Select
                        options={relativeTimingOptions.map((option) => ({
                            ...option,
                            disabled:
                                option.value === RelativeTiming.before ?
                                    shouldDisableBeforeOptionForRelativeTiming
                                :   false,
                        }))}
                    />
                </FormItem>
                <FormItem
                    name={[parentFieldName, MULTI_SELECTOR_KEY]}
                    style={{ minWidth: 200, width: '100%', flex: 1, marginBottom: 0, marginTop: 0 }}
                >
                    <Select options={lifecycleEventOptions} />
                </FormItem>
                <FormItem name={[parentFieldName, 'lifecycleEventType']} noStyle style={{ width: 0, height: 0 }} />
                <FormItem name={[parentFieldName, 'waitForAllDestinations']} noStyle style={{ width: 0, height: 0 }} />
            </div>

            {shouldDisableBeforeOptionForRelativeTiming && (
                <div className="ant-form-item-explain" style={{ color: runwayColors.statusGrey }}>
                    <ul
                        style={{
                            paddingLeft: 16,
                            fontSize: '12px',
                            paddingBottom: 0,
                            marginBottom: 0,
                            marginTop: 10,
                            color: runwayColors.statusGrey,
                        }}
                    >
                        <li>
                            Setting target times before lifecycle events requires a schedule cadence to be set in
                            Schedule settings.
                        </li>
                    </ul>
                </div>
            )}
            {showAllowTriggeringBasedOnTargetDate && selectedTiming !== RelativeTiming.before && (
                <FormItem
                    name={[parentFieldName, 'allowTriggeringBasedOnTargetDate']}
                    valuePropName="checked"
                    style={{ marginBottom: 0, marginTop: 12 }}
                >
                    <Checkbox>
                        Allow triggering based on target date
                        <Tooltip title="If configured to trigger on or after an event, allow triggering based on the target date even if the event hasn't occurred yet">
                            <InfoCircleOutlined style={{ color: runwayColors.statusGrey, marginLeft: 8 }} />
                        </Tooltip>
                    </Checkbox>
                </FormItem>
            )}
            {isOrgLevelItem && (
                <div className="ant-form-item-explain" style={{ color: runwayColors.statusGrey }}>
                    <ul
                        style={{
                            paddingLeft: 16,
                            fontSize: '12px',
                            paddingBottom: 0,
                            marginBottom: 0,
                            marginTop: 10,
                            color: runwayColors.statusGrey,
                        }}
                    >
                        <li>
                            Setting target times relative to a lifecycle event will only work if the lifecycle event is
                            configured to exist for each applicable step.
                        </li>
                        {selectedTiming === RelativeTiming.before && (
                            <li>
                                Setting target times before lifecycle events requires a schedule cadence to be set in
                                every app's Schedule settings.
                            </li>
                        )}
                        {selectedLifecycleEventType === LifecycleEventType.LifecycleEventTypeRelease && (
                            <li>
                                Note that reminders for Android releases set relative to the Release event will not
                                work. More information is available{' '}
                                <a href="https://docs.runway.team/using-runway/release-steps/app-submission#android-release-settings">
                                    here
                                </a>
                                .
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

const timeInputStyle: React.CSSProperties = {
    width: 70,
    textAlign: 'center',
    borderRadius: 8,
};
